import React, { useState, useCallback, lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from './contexts/ThemeContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoadingSpinner } from './components/LoadingSpinner';
import Header from './components/Header';
import SegmentedControl from './components/SegmentedControl';
import Timeline from './components/Timeline';
import Footer from './components/Footer';
import FloatingThemeButton from './components/FloatingThemeButton';
import CategoryTag from './components/CategoryTag';
import CanonicalLink from './components/CanonicalLink';
import StructuredData from './components/StructuredData';
import { generateWebSiteSchema, generateOrganizationSchema } from './utils/schemaGenerator';
import { CATEGORIES, PLATFORMS } from './utils/constants';
import { trackPlatformFilter, trackCategoryFilter, trackCategoryFiltersCleared, trackSlowFilterResponse, measurePerformance } from './utils/analytics';
import './App.css';

// Lazy load pages for better performance
const IndieDevToolkit = lazy(() => import('./pages/IndieDevToolkit'));
const About = lazy(() => import('./pages/About'));
const Contribute = lazy(() => import('./pages/Contribute'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Timeline component with URL state management
const TimelinePage = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [activeCategoryFilters, setActiveCategoryFilters] = useState(new Set());
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize filters from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const platformParam = params.get('platform');
    const categoriesParam = params.get('categories');

    if (platformParam) {
      // Match case-insensitively against the known platform list (e.g. "ipados" -> "iPadOS")
      const matchedPlatform = PLATFORMS.find(p => p.toLowerCase() === platformParam.toLowerCase());
      if (matchedPlatform) {
        setActiveFilter(matchedPlatform);
      }
    }

    if (categoriesParam) {
      // Drop any values that aren't real categories (e.g. stale/edited links)
      const categories = categoriesParam.split(',').filter(cat => CATEGORIES.includes(cat));
      if (categories.length > 0) {
        setActiveCategoryFilters(new Set(categories));
      }
    }

    setIsInitialized(true);
  }, []);

  // Update URL when platform filter changes
  useEffect(() => {
    if (!isInitialized) return;

    const params = new URLSearchParams(window.location.search);

    if (activeFilter === 'All') {
      params.delete('platform');
    } else {
      params.set('platform', activeFilter.toLowerCase());
    }

    const newUrl = params.toString() ? `?${params.toString()}` : '/';
    window.history.replaceState({}, '', newUrl);
  }, [activeFilter, isInitialized]);

  // Update URL when category filters change
  useEffect(() => {
    if (!isInitialized) return;

    const params = new URLSearchParams(window.location.search);

    if (activeCategoryFilters.size === 0) {
      params.delete('categories');
    } else {
      params.set('categories', Array.from(activeCategoryFilters).join(','));
    }

    const newUrl = params.toString() ? `?${params.toString()}` : '/';
    window.history.replaceState({}, '', newUrl);
  }, [activeCategoryFilters, isInitialized]);

  // Memoize callback to prevent unnecessary re-renders in child components
  const handleFilterChange = useCallback((filter) => {
    const startTime = performance.now();
    setActiveFilter(filter);
    // Track platform filter change
    if (isInitialized) {
      trackPlatformFilter(filter);
      // Track slow filter response
      requestAnimationFrame(() => {
        const duration = measurePerformance(startTime);
        if (duration > 500) {
          trackSlowFilterResponse('platform', duration);
        }
      });
    }
  }, [isInitialized]);

  // Memoize category filter toggle
  const handleCategoryFilterToggle = useCallback((category) => {
    const startTime = performance.now();
    setActiveCategoryFilters(prev => {
      const newFilters = new Set(prev);
      const action = newFilters.has(category) ? 'removed' : 'added';
      if (newFilters.has(category)) {
        newFilters.delete(category);
      } else {
        newFilters.add(category);
      }
      // Track category filter toggle
      if (isInitialized) {
        trackCategoryFilter(category, action);
        // Track slow filter response
        requestAnimationFrame(() => {
          const duration = measurePerformance(startTime);
          if (duration > 500) {
            trackSlowFilterResponse('category', duration);
          }
        });
      }
      return newFilters;
    });
  }, [isInitialized]);

  // Memoize clear filters function
  const clearCategoryFilters = useCallback(() => {
    const count = activeCategoryFilters.size;
    setActiveCategoryFilters(new Set());
    // Track clearing all category filters
    if (count > 0) {
      trackCategoryFiltersCleared(count);
    }
  }, [activeCategoryFilters]);

  return (
    <main className="timeline-container">
      <StructuredData data={[generateWebSiteSchema(), generateOrganizationSchema()]} />
      <nav aria-label="Platform filter">
        <SegmentedControl activeFilter={activeFilter} onFilterChange={handleFilterChange} />
      </nav>
      {activeCategoryFilters.size > 0 && (
        <aside className="category-filter-indicator">
          <div className="category-filters-section">
            <span className="filter-label">Categories:</span>
            <div className="category-tags-container">
              {CATEGORIES.map(category => (
                <CategoryTag
                  key={category}
                  category={category}
                  isActive={activeCategoryFilters.has(category)}
                  onClick={handleCategoryFilterToggle}
                  size="normal"
                />
              ))}
            </div>
          </div>
          <button className="clear-filters-btn" onClick={clearCategoryFilters}>
            Clear All ({activeCategoryFilters.size})
          </button>
        </aside>
      )}
      <Timeline
        activeFilter={activeFilter}
        activeCategoryFilters={activeCategoryFilters}
        onCategoryFilterToggle={handleCategoryFilterToggle}
      />
    </main>
  );
};

function App() {

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider>
          <Router>
            <div className="App">
              <CanonicalLink />
              <Header />
            <Routes>
              <Route path="/" element={
                <ErrorBoundary>
                  <TimelinePage />
                </ErrorBoundary>
              } />
              <Route path="/indie-dev-toolkit" element={
                <ErrorBoundary>
                  <Suspense fallback={<LoadingSpinner message="Loading Indie Dev Toolkit..." />}>
                    <IndieDevToolkit />
                  </Suspense>
                </ErrorBoundary>
              } />
              <Route path="/about" element={
                <ErrorBoundary>
                  <About />
                </ErrorBoundary>
              } />
              <Route path="/contribute" element={
                <ErrorBoundary>
                  <Suspense fallback={<LoadingSpinner message="Loading Contribute page..." />}>
                    <Contribute />
                  </Suspense>
                </ErrorBoundary>
              } />
              <Route path="*" element={
                <ErrorBoundary>
                  <Suspense fallback={<LoadingSpinner message="Loading..." />}>
                    <NotFound />
                  </Suspense>
                </ErrorBoundary>
              } />
            </Routes>
            <Footer />
            <FloatingThemeButton />
          </div>
        </Router>
      </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;